import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function pct(num: number, den: number): string {
  if (!den) return "—";
  return `${Math.round((num / den) * 100)}%`;
}

/**
 * Funil de prospecção por SDR: recebidos → abordados → respondeu → reunião.
 * É a tabela do ritual semanal — substitui o "quantos você tentou?".
 */
export function ProspectingReport({
  pipelineId,
  canViewAll,
  users,
}: {
  pipelineId: number;
  canViewAll: boolean;
  users: Array<{ id: number; name: string | null; email: string | null }>;
}) {
  const hoje = new Date();
  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const [from, setFrom] = useState(isoDate(inicioMes));
  const [to, setTo] = useState(isoDate(hoje));
  const [ownerUserId, setOwnerUserId] = useState<string>("all");
  const [batchId, setBatchId] = useState<string>("all");

  const batchesQuery = trpc.contacts.importsList.useQuery({ kind: "import", limit: 50 });
  const batches = (batchesQuery.data?.batches ?? []) as any[];

  const input = useMemo(
    () => ({
      pipelineId,
      from: new Date(`${from}T00:00:00`),
      to: new Date(`${to}T23:59:59.999`),
      ownerUserId: ownerUserId !== "all" ? Number(ownerUserId) : undefined,
      importBatchId: batchId !== "all" ? Number(batchId) : undefined,
    }),
    [pipelineId, from, to, ownerUserId, batchId],
  );
  const report = trpc.prospecting.report.summary.useQuery(input);
  const byScript = trpc.prospecting.report.byScript.useQuery({
    pipelineId: input.pipelineId, from: input.from, to: input.to, ownerUserId: input.ownerUserId,
  });

  const total = report.data?.total;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Até</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" />
        </div>
        {canViewAll && (
          <div className="space-y-1">
            <Label className="text-xs">SDR</Label>
            <Select value={ownerUserId} onValueChange={setOwnerUserId}>
              <SelectTrigger className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>{u.name ?? u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {batches.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs">Lista importada</Label>
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger className="h-9 w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {batches.map((b: any) => (
                  <SelectItem key={b.id} value={String(b.id)}>{b.displayName ?? b.fileName ?? `Lote #${b.id}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {report.isLoading || !total ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Stat label="Recebidos" value={total.recebidos} />
            <Stat label="Abordados" value={total.abordados} sub={pct(total.abordados, total.recebidos) + " dos recebidos"} />
            <Stat label="Tentativas" value={total.tentativas} sub="no período" />
            <Stat label="Responderam" value={total.respondeu} sub={pct(total.respondeu, total.abordados) + " dos abordados"} tone="emerald" />
            <Stat label="Reuniões" value={total.reuniao} sub={pct(total.reuniao, total.respondeu) + " dos que responderam"} tone="primary" />
            <Stat label="Sem resposta" value={total.semResposta} sub={`${total.semInteresse} sem interesse · ${total.numeroInvalido} inválidos`} tone="muted" />
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">SDR</th>
                    <th className="text-right px-3 py-2 font-medium">Recebidos</th>
                    <th className="text-right px-3 py-2 font-medium">Abordados</th>
                    <th className="text-right px-3 py-2 font-medium">Tentativas</th>
                    <th className="text-right px-3 py-2 font-medium">Respondeu</th>
                    <th className="text-right px-3 py-2 font-medium">Reunião</th>
                    <th className="text-right px-3 py-2 font-medium">Sem resposta</th>
                    <th className="text-right px-3 py-2 font-medium">Sem interesse</th>
                    <th className="text-right px-3 py-2 font-medium">Inválido</th>
                    <th className="text-right px-3 py-2 font-medium">Em andamento</th>
                    <th className="text-right px-3 py-2 font-medium">Taxa resp.</th>
                    <th className="text-right px-3 py-2 font-medium">Taxa reunião</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data!.rows.length === 0 ? (
                    <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">Nenhum lead no período.</td></tr>
                  ) : (
                    report.data!.rows.map((r) => (
                      <tr key={String(r.ownerUserId ?? "none")} className="border-t">
                        <td className="px-3 py-2 font-medium">{r.ownerName ?? "Sem dono"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.recebidos}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.abordados}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.tentativas}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.respondeu}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.reuniao}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.semResposta}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.semInteresse}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.numeroInvalido}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.emAndamento}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(r.respondeu, r.abordados)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(r.reuniao, r.abordados)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {report.data!.rows.length > 1 && (
                  <tfoot className="border-t bg-muted/30 font-medium">
                    <tr>
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.recebidos}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.abordados}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.tentativas}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.respondeu}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.reuniao}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.semResposta}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.semInteresse}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.numeroInvalido}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{total.emAndamento}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(total.respondeu, total.abordados)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(total.reuniao, total.abordados)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <div className="px-3 pt-3 pb-1">
                <div className="text-sm font-medium">Qual roteiro funciona melhor</div>
                <div className="text-[11px] text-muted-foreground">
                  Cada tentativa recebe o desfecho que o SDR marcou depois dela. Compare roteiros da mesma tentativa.
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Roteiro</th>
                    <th className="text-left px-3 py-2 font-medium">Tentativa</th>
                    <th className="text-right px-3 py-2 font-medium">Envios</th>
                    <th className="text-right px-3 py-2 font-medium">Respondeu</th>
                    <th className="text-right px-3 py-2 font-medium">Reunião</th>
                    <th className="text-right px-3 py-2 font-medium">Sem interesse</th>
                    <th className="text-right px-3 py-2 font-medium">Taxa resp.</th>
                    <th className="text-right px-3 py-2 font-medium">Taxa reunião</th>
                  </tr>
                </thead>
                <tbody>
                  {(byScript.data ?? []).length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nenhuma abordagem no período.</td></tr>
                  ) : (
                    (byScript.data ?? []).map((r) => (
                      <tr key={String(r.scriptId ?? "none")} className="border-t">
                        <td className="px-3 py-2 font-medium">{r.scriptName ?? "Sem roteiro"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.attemptNo ? `${r.attemptNo}ª` : "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.tentativas}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.respondeu}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.reuniao}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.semInteresse}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{pct(r.respondeu, r.tentativas)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(r.reuniao, r.tentativas)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <p className="text-[11px] text-muted-foreground">
            Recebidos = leads que entraram na fila no período. Respondeu inclui quem depois agendou reunião.
            Tentativas conta todas as abordagens feitas no período, de qualquer lead.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone = "default" }: { label: string; value: number; sub?: string; tone?: "default" | "emerald" | "primary" | "muted" }) {
  const cls = {
    default: "",
    emerald: "text-emerald-600",
    primary: "text-primary",
    muted: "text-muted-foreground",
  }[tone];
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
      </CardContent>
    </Card>
  );
}
